import type {
  GovernorLiveAction,
} from '@/lib/governorEventBus';

export type CallsLifecycleCertificationStatus =
  | 'certified'
  | 'waiting'
  | 'failed';

export type CallsLifecycleEvidence = Readonly<{
  mediaCertifiedAt: string | null;
  conversationCertifiedAt: string | null;
  conversationEndedAt: string | null;
  returnedToMessagesAt: string | null;
}>;

export type CallsEndToEndLifecycleCertification =
  Readonly<{
    status: CallsLifecycleCertificationStatus;
    certified: boolean;
    correlationKey: string | null;
    summary: string;
    missingStages: readonly string[];
    evidence: CallsLifecycleEvidence;
    failureStage: string | null;
    evaluatedAt: string;
  }>;

const FAILURE_STAGES = new Set([
  'CALL_RUNTIME_START_FAILED',
  'PEER_CONNECTION_FAILED',
  'CALL_CONNECTION_TIMEOUT',
  'MEDIA_PATH_CERTIFICATION_TIMEOUT',
  'REMOTE_MEDIA_STREAM_MISSING',
  'CONNECTION_SESSION_SYNC_FAILED',
]);

function timestampOf(
  action: GovernorLiveAction,
): string {
  return String(
    action?.detail?.timestamp ||
      action?.createdAt ||
      '',
  );
}

function epochOf(
  action: GovernorLiveAction,
): number {
  const parsed = Date.parse(timestampOf(action));
  return Number.isFinite(parsed) ? parsed : 0;
}

function stageOf(
  action: GovernorLiveAction,
): string {
  return String(
    action?.detail?.stage ||
      action?.detail?.step ||
      '',
  );
}

function correlationKeyOf(
  action: GovernorLiveAction,
): string | null {
  const detail = action?.detail || {};

  const direct =
    detail.callSessionId ||
    detail.call_session_id ||
    detail.roomName ||
    null;

  if (direct) return String(direct);

  const first = String(
    detail.selfEmail ||
      detail.callerEmail ||
      detail.receiverEmail ||
      '',
  )
    .trim()
    .toLowerCase();

  const second = String(
    detail.peerEmail ||
      detail.receiverEmail ||
      detail.callerEmail ||
      '',
  )
    .trim()
    .toLowerCase();

  const participants = [first, second]
    .filter(Boolean)
    .sort();

  return participants.length === 2
    ? participants.join('|')
    : null;
}

function sameLifecycle(
  action: GovernorLiveAction,
  correlationKey: string | null,
): boolean {
  if (!correlationKey) return true;

  const key = correlationKeyOf(action);

  return key === null || key === correlationKey;
}

function latestMatching(
  actions: readonly GovernorLiveAction[],
  predicate: (
    action: GovernorLiveAction,
  ) => boolean,
  correlationKey: string | null,
): GovernorLiveAction | null {
  return (
    actions.find(
      action =>
        predicate(action) &&
        sameLifecycle(action, correlationKey),
    ) || null
  );
}

export function certifyCallsEndToEndLifecycle(
  sourceActions: readonly GovernorLiveAction[] = [],
): CallsEndToEndLifecycleCertification {
  const actions = [...sourceActions].sort(
    (a, b) => epochOf(b) - epochOf(a),
  );

  const returnAction =
    actions.find(action => {
      const detail = action?.detail || {};

      return (
        stageOf(action) ===
          'CALL_RETURNED_TO_MESSAGES' ||
        (
          action?.type ===
            'governor_native_call_action_consumed' &&
          detail.action === 'end' &&
          detail.status === 'ended' &&
          detail.returnedSurface ===
            'family-messages'
        )
      );
    }) || null;

  const correlationKey =
    returnAction === null
      ? null
      : correlationKeyOf(returnAction);

  const mediaAction = latestMatching(
    actions,
    action =>
      stageOf(action) ===
        'MEDIA_PATH_CERTIFIED' &&
      action?.detail?.status === 'success',
    correlationKey,
  );

  const conversationAction = latestMatching(
    actions,
    action =>
      stageOf(action) ===
        'CONVERSATION_WINDOW_CERTIFIED' &&
      action?.detail?.status === 'success',
    correlationKey,
  );

  const endedAction = latestMatching(
    actions,
    action =>
      stageOf(action) ===
        'CALL_CONVERSATION_ENDED',
    correlationKey,
  );

  const failureAction = latestMatching(
    actions,
    action =>
      FAILURE_STAGES.has(stageOf(action)) ||
      action?.detail?.status === 'failure' ||
      action?.detail?.status === 'timeout',
    correlationKey,
  );

  const evidence = Object.freeze({
    mediaCertifiedAt:
      mediaAction === null
        ? null
        : timestampOf(mediaAction),
    conversationCertifiedAt:
      conversationAction === null
        ? null
        : timestampOf(conversationAction),
    conversationEndedAt:
      endedAction === null
        ? null
        : timestampOf(endedAction),
    returnedToMessagesAt:
      returnAction === null
        ? null
        : timestampOf(returnAction),
  });

  const missingStages = [
    mediaAction
      ? null
      : 'MEDIA_PATH_CERTIFIED',
    conversationAction
      ? null
      : 'CONVERSATION_WINDOW_CERTIFIED',
    endedAction
      ? null
      : 'CALL_CONVERSATION_ENDED',
    returnAction
      ? null
      : 'CALL_RETURNED_TO_MESSAGES',
  ].filter(Boolean) as string[];

  const ordered =
    mediaAction !== null &&
    conversationAction !== null &&
    endedAction !== null &&
    returnAction !== null &&
    epochOf(mediaAction) <=
      epochOf(conversationAction) &&
    epochOf(conversationAction) <=
      epochOf(endedAction) &&
    epochOf(endedAction) <=
      epochOf(returnAction);

  const failureAfterMedia =
    failureAction !== null &&
    (
      mediaAction === null ||
      epochOf(failureAction) >=
        epochOf(mediaAction)
    );

  if (failureAfterMedia) {
    return Object.freeze({
      status: 'failed',
      certified: false,
      correlationKey,
      summary:
        'The latest correlated Calls lifecycle contains decisive failure evidence.',
      missingStages: Object.freeze([
        ...missingStages,
      ]),
      evidence,
      failureStage: stageOf(failureAction),
      evaluatedAt: new Date().toISOString(),
    });
  }

  if (
    missingStages.length === 0 &&
    ordered
  ) {
    return Object.freeze({
      status: 'certified',
      certified: true,
      correlationKey,
      summary:
        'Live Calls evidence proves certified media, a stable conversation, clean termination, and return to Family Messages.',
      missingStages: Object.freeze([]),
      evidence,
      failureStage: null,
      evaluatedAt: new Date().toISOString(),
    });
  }

  return Object.freeze({
    status: 'waiting',
    certified: false,
    correlationKey,
    summary:
      missingStages.length > 0
        ? `Calls lifecycle is awaiting: ${missingStages.join(
            ', ',
          )}.`
        : 'Calls lifecycle evidence exists but is not in authoritative chronological order.',
    missingStages: Object.freeze([
      ...missingStages,
    ]),
    evidence,
    failureStage: null,
    evaluatedAt: new Date().toISOString(),
  });
}
